import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getUnseenWhatsNewCount } from '@lib/api';
import { toProblem } from '@lib/api/base';

interface WhatsNewContextType {
  unseenCount: number;
  isLoading: boolean;
  error: string | null;
  refreshCount: () => Promise<void>;
  decrementCount: (amount: number) => void;
}

const WhatsNewContext = createContext<WhatsNewContextType | undefined>(undefined);

export const useWhatsNew = () => {
  const context = useContext(WhatsNewContext);
  if (context === undefined) {
    throw new Error('useWhatsNew must be used within a WhatsNewProvider');
  }
  return context;
};

interface WhatsNewProviderProps {
  children: React.ReactNode;
}

export const WhatsNewProvider: React.FC<WhatsNewProviderProps> = ({ children }) => {
  const [unseenCount, setUnseenCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshCount = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await getUnseenWhatsNewCount();
      setUnseenCount(result.unseenCount ?? 0);
    } catch (err) {
      const problem = toProblem(err);
      console.error('Error fetching unseen count:', problem);
      setError(problem.detail || problem.title || 'Failed to load');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const decrementCount = useCallback((amount: number) => {
    setUnseenCount(prev => Math.max(0, prev - amount));
  }, []);

  // Initial load
  useEffect(() => {
    refreshCount();

    // Set up polling every 5 minutes to keep the badge updated
    const intervalId = setInterval(refreshCount, 5 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [refreshCount]);

  const value: WhatsNewContextType = {
    unseenCount,
    isLoading,
    error,
    refreshCount,
    decrementCount,
  };

  return <WhatsNewContext.Provider value={value}>{children}</WhatsNewContext.Provider>;
};
